/**
 * Participation Summary — the executive-team-member leaf set (SPEC-02 §3.3).
 *
 * Answers "what participation is asked of MY teams?" and lets the answer be
 * given right there: capability / capacity / commitment tristates on the same
 * `planning_team_participation` rows B Mode edits (Doc 26 rule 7 — view,
 * never schema). Team selection is a per-device viewing preference
 * (localStorage — UI pref only, not authority state).
 *
 * Pruning (rule 2): the whole card is absent when the workspace has no
 * participation requirements.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, CheckCircle2, Loader2, Search, Users } from 'lucide-react';
import {
  getPriorities,
  getTeamParticipation,
  getWorkUnits,
  updateTeamParticipation,
} from '../../lib/planningEngineService';
import { getItemCountState } from '../../components/collection/types';
import type {
  ParticipationTristate,
  TeamParticipation,
} from '../../lib/planningEngineModule.types';

// ---------------------------------------------------------------------------
// Plain-language tristate questions (Doc 26 rule 5)
// ---------------------------------------------------------------------------

const TRISTATE_FIELDS = [
  { key: 'capabilityPresent', label: 'Has the skill' },
  { key: 'capacityConfirmed', label: 'Has the time' },
  { key: 'committed', label: 'Committed' },
] as const;

type TristateKey = (typeof TRISTATE_FIELDS)[number]['key'];

const TRISTATE_OPTIONS: { value: ParticipationTristate; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'partial', label: 'Partial' },
  { value: 'no', label: 'No' },
];

function tristateClass(value: ParticipationTristate, selected: boolean): string {
  if (!selected) {
    return 'bg-[var(--bg-raised)] dark:bg-transparent text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]';
  }
  if (value === 'yes') return 'bg-[var(--green)] text-white';
  if (value === 'partial') return 'bg-[var(--mustard)] text-white';
  return 'bg-[var(--coral)] text-white';
}

function isOutstanding(row: TeamParticipation): boolean {
  return row.capabilityPresent !== 'yes' || row.capacityConfirmed !== 'yes' || row.committed !== 'yes';
}

// ---------------------------------------------------------------------------
// My-teams viewing preference (per device, per workspace)
// ---------------------------------------------------------------------------

function myTeamsKey(projectId: string): string {
  return `planning:myTeams:${projectId}`;
}

function loadMyTeams(projectId: string): string[] | null {
  try {
    const raw = localStorage.getItem(myTeamsKey(projectId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : null;
  } catch {
    return null;
  }
}

function saveMyTeams(projectId: string, teams: string[]): void {
  try {
    localStorage.setItem(myTeamsKey(projectId), JSON.stringify(teams));
  } catch {
    // Private-mode failures degrade to session-only selection.
  }
}

// ---------------------------------------------------------------------------
// Row with confirm/decline controls
// ---------------------------------------------------------------------------

function ParticipationRow({
  row,
  scopeLabel,
  onUpdate,
}: {
  row: TeamParticipation;
  scopeLabel: string;
  onUpdate: (id: string, field: TristateKey, value: ParticipationTristate) => Promise<void>;
}) {
  const [savingField, setSavingField] = useState<TristateKey | null>(null);

  const change = async (field: TristateKey, value: ParticipationTristate) => {
    if (row[field] === value || savingField) return;
    setSavingField(field);
    try {
      await onUpdate(row.id, field, value);
    } finally {
      setSavingField(null);
    }
  };

  return (
    <div className="px-4 py-3 border-b border-[var(--rule)] dark:border-[var(--rule)] last:border-0">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">
            {scopeLabel}
          </p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] mt-0.5">
            {row.requiredCapability ? `Needs: ${row.requiredCapability}` : 'Participation required'}
            {row.capacityRequiredWu != null ? ` · ${row.capacityRequiredWu} WU` : ''}
            {row.owner ? ` · Owner: ${row.owner}` : ''}
          </p>
          {row.riskIfUnavailable && (
            <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)] mt-0.5">
              If unavailable: {row.riskIfUnavailable}
            </p>
          )}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          {TRISTATE_FIELDS.map(({ key, label }) => (
            <div key={key} className="flex flex-col items-start gap-1">
              <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                {label}
              </span>
              <div
                className="inline-flex border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden"
                role="group"
                aria-label={`${label} for ${scopeLabel}`}
              >
                {TRISTATE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={savingField !== null}
                    onClick={() => void change(key, opt.value)}
                    className={`px-2 py-1 text-xs font-medium transition-colors border-r border-[var(--rule)] dark:border-[var(--rule)] last:border-r-0 disabled:opacity-60 ${tristateClass(opt.value, row[key] === opt.value)}`}
                  >
                    {savingField === key && row[key] !== opt.value ? (
                      opt.label
                    ) : savingField === key ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      opt.label
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export default function ParticipationSummaryCard({
  projectId,
  userName,
  onChanged,
}: {
  projectId: string;
  userName: string;
  /** Called after a confirm/decline lands, so the dashboard can re-check feasibility. */
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<TeamParticipation[]>([]);
  const [scopeNames, setScopeNames] = useState<Map<string, string>>(new Map());
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [myTeams, setMyTeams] = useState<string[] | null>(() => loadMyTeams(projectId));
  const [query, setQuery] = useState('');
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [participation, priorities, workUnits] = await Promise.all([
          getTeamParticipation(projectId),
          getPriorities(projectId),
          getWorkUnits(projectId),
        ]);
        if (cancelled) return;
        const names = new Map<string, string>();
        for (const p of priorities) names.set(p.id, p.name);
        for (const wu of workUnits) names.set(wu.id, wu.title);
        setRows(participation);
        setScopeNames(names);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load participation');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const allTeams = useMemo(
    () => [...new Set(rows.map((r) => r.requiredTeam))].sort((a, b) => a.localeCompare(b)),
    [rows],
  );

  // Default team selection: teams whose rows name the current user as owner.
  // Falls back to all teams so the card is useful before any preference is set.
  const selectedTeams = useMemo(() => {
    if (myTeams && myTeams.length > 0) return myTeams.filter((t) => allTeams.includes(t));
    const owned = allTeams.filter((team) =>
      rows.some(
        (r) =>
          r.requiredTeam === team &&
          r.owner != null &&
          userName.trim().length > 0 &&
          r.owner.toLowerCase().includes(userName.trim().toLowerCase()),
      ),
    );
    return owned.length > 0 ? owned : allTeams;
  }, [myTeams, allTeams, rows, userName]);

  const toggleTeam = (team: string) => {
    const next = selectedTeams.includes(team)
      ? selectedTeams.filter((t) => t !== team)
      : [...selectedTeams, team];
    // Never persist an empty selection — an empty screen is a dead end.
    if (next.length === 0) return;
    setMyTeams(next);
    saveMyTeams(projectId, next);
  };

  const scopeLabelFor = useCallback((row: TeamParticipation): string => {
    const priorityName = row.priorityId ? scopeNames.get(row.priorityId) : null;
    if (row.workUnitId) {
      const wuName = scopeNames.get(row.workUnitId);
      return wuName ? `${priorityName ?? 'Priority'} — ${wuName}` : (priorityName ?? 'Work unit');
    }
    return priorityName ? `${priorityName} (all its work)` : 'All work';
  }, [scopeNames]);

  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows
      .filter((r) => selectedTeams.includes(r.requiredTeam))
      .filter((r) => !q || scopeLabelFor(r).toLowerCase().includes(q) || (r.requiredCapability ?? '').toLowerCase().includes(q))
      .sort((a, b) => Number(isOutstanding(b)) - Number(isOutstanding(a)) || a.requiredTeam.localeCompare(b.requiredTeam));
  }, [rows, selectedTeams, query, scopeLabelFor]);

  const byTeam = useMemo(() => {
    const m = new Map<string, TeamParticipation[]>();
    for (const r of visibleRows) {
      m.set(r.requiredTeam, [...(m.get(r.requiredTeam) ?? []), r]);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [visibleRows]);

  const outstandingCount = visibleRows.filter(isOutstanding).length;

  const handleUpdate = useCallback(
    async (id: string, field: TristateKey, value: ParticipationTristate) => {
      try {
        const updated = await updateTeamParticipation(id, { [field]: value });
        setRows((prev) => prev.map((r) => (r.id === id ? updated : r)));
        setError(null);
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2500);
        onChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save');
      }
    },
    [onChanged],
  );

  // Pruned entirely when there is nothing asked of any team (rule 2).
  if (!loaded || rows.length === 0) return null;

  return (
    <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-semibold text-[var(--text-ter)] dark:text-[var(--text-sec)] uppercase tracking-wider">
              What's asked of your teams
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {justSaved && (
              <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                <Check className="h-3.5 w-3.5" /> Saved — rechecking feasibility
              </span>
            )}
            {getItemCountState(visibleRows.length) === 'many' && (
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--text-ter)]" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search requirements…"
                  className="pl-8 pr-3 py-1.5 text-sm border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-primary-500 w-52"
                />
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
          {outstandingCount > 0
            ? `${outstandingCount} of ${visibleRows.length} requirement${visibleRows.length === 1 ? '' : 's'} still need${outstandingCount === 1 ? 's' : ''} an answer. Confirming here updates the plan for everyone.`
            : 'Everything asked of these teams is confirmed and committed.'}
        </p>
        {/* Team chips — a viewing filter, not a permission */}
        {allTeams.length > 1 && (
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {allTeams.map((team) => {
              const active = selectedTeams.includes(team);
              const teamOutstanding = rows.filter((r) => r.requiredTeam === team && isOutstanding(r)).length;
              return (
                <button
                  key={team}
                  type="button"
                  onClick={() => toggleTeam(team)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                    active
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-[var(--bg-raised)] dark:bg-transparent border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:border-[var(--rule)]'
                  }`}
                >
                  {team}
                  {teamOutstanding > 0 && (
                    <span className={active ? 'ml-1 opacity-80' : 'ml-1 text-[var(--mustard)] dark:text-[var(--mustard)]'}>
                      · {teamOutstanding}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {error && (
        <div className="mx-5 mb-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] px-3 py-2 text-xs text-[var(--coral)] dark:text-[var(--coral)]">
          {error}
        </div>
      )}

      {byTeam.map(([team, teamRows]) => (
        <div key={team}>
          <div className="px-4 py-1.5 bg-[var(--surface)] dark:bg-[var(--bg)] flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">{team}</span>
            {teamRows.every((r) => !isOutstanding(r)) && (
              <span className="flex items-center gap-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                <CheckCircle2 className="h-3.5 w-3.5" /> All confirmed
              </span>
            )}
          </div>
          {teamRows.map((row) => (
            <ParticipationRow
              key={row.id}
              row={row}
              scopeLabel={scopeLabelFor(row)}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      ))}

      {visibleRows.length === 0 && (
        <p className="px-4 py-6 text-sm text-[var(--text-ter)] text-center">
          {query ? `No requirements match "${query}".` : 'No requirements for the selected teams.'}
        </p>
      )}

      <div className="px-5 py-3 border-t border-[var(--rule)] dark:border-[var(--rule)]">
        <Link
          to="/planning/engine/participation"
          className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          Add or edit requirements in the workbench
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
